import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getSearchQuery,
  SEARCH_QUERY_PARAM,
  withSearchQuery,
} from '@/lib/search-navigation';
import type { SortState } from '@/types';

interface UsePaginationOptions {
  initialPage?: number;
  initialPerPage?: number;
  initialSort?: SortState;
}

export function usePagination(options: UsePaginationOptions = {}) {
  const [page, setPage] = useState(options.initialPage ?? 1);
  const [perPage] = useState(options.initialPerPage ?? 50);
  const [searchParams, setSearchParams] = useSearchParams();
  const search = getSearchQuery(searchParams);
  const [sort, setSortState] = useState<SortState>(
    options.initialSort ?? { field: 'title', direction: 'asc' },
  );

  const setSearch = useCallback(
    (value: string) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value) {
            next.set(SEARCH_QUERY_PARAM, value);
          } else {
            next.delete(SEARCH_QUERY_PARAM);
          }
          return next;
        },
        { replace: true },
      );
      setPage(1);
    },
    [setSearchParams],
  );

  const getSearchPath = useCallback(
    (path: string) => withSearchQuery(path, search),
    [search],
  );

  const setSort = useCallback((value: SortState) => {
    setSortState(value);
    setPage(1);
  }, []);

  return {
    page,
    perPage,
    search,
    sort,
    setPage,
    setSearch,
    setSort,
    getSearchPath,
  };
}
